<template>
	<v-container>
		<v-row class="contentRow">
			<v-col cols="12">
				<h1>Block #{{ index || "" }}</h1>
				<v-list-item v-if="result">
					<v-list-item-content>
						<h5 class="secondary--text">Hash</h5>
						<v-list-item-title>{{result.Hash}}</v-list-item-title>
					</v-list-item-content>
				</v-list-item>
				<v-list-item>
					<v-list-item-content v-if="result">
						<h5 class="secondary--text">Timestamp</h5>
						<v-list-item-title>{{this.$moment.unix(result.Timestamp).fromNow()}}</v-list-item-title>
					</v-list-item-content>
				</v-list-item>
				<v-list-item>
					<v-list-item-content v-if="result">
						<h5 class="secondary--text">Previous Hash</h5>
						<v-list-item-title>{{result.PreviousHash}}</v-list-item-title>
					</v-list-item-content>
				</v-list-item>
			</v-col>
		</v-row>
		<v-row class="contentRow" v-if="!!result && dataunits.length == 0">
			<v-col cols="12">
				<h2>No Data Units</h2>
				<p>There are no data units in this block.</p>
			</v-col>
		</v-row>
		<v-row class="contentRow" v-for="(item, index) in dataunits" :key="item.Hash || index">
			<v-col cols="3">
				<h2>Data Unit {{index+1}}</h2>
				<v-list-item>
					<v-list-item-content>
						<h5 class="secondary--text">Timestamp</h5>
						<v-list-item-title>{{timestamp_text(item.Timestamp)}}</v-list-item-title>
					</v-list-item-content>
				</v-list-item>
				<v-list-item>
					<v-list-item-content>
						<h5 class="secondary--text">Type</h5>
						<v-list-item-title v-if="item.Type == 'tx'">Orixe Transaction</v-list-item-title>
						<v-list-item-title v-else-if="item.Type == 'audit'">Orixe Audit</v-list-item-title>
						<v-list-item-title v-else-if="item.Type == 'esg'">ESG Form</v-list-item-title>
						<v-list-item-title v-else-if="item.Type == 'dude'">DUDE</v-list-item-title>
						<v-list-item-title v-else-if="item.Type == 'authorized'">Authorized Node</v-list-item-title>
						<v-list-item-title v-else>{{item.Type}}</v-list-item-title>
					</v-list-item-content>
				</v-list-item>
				<v-list-item>
					<v-list-item-content>
						<h5 class="secondary--text">Location</h5>
						<v-list-item-title>{{item.Location}}</v-list-item-title>
					</v-list-item-content>
				</v-list-item>
			</v-col>
			<v-col cols="9">
				<v-list-item>
					<v-list-item-content>
						<h5 class="secondary--text">Hash</h5>
						<v-list-item-title>{{item.Hash}}</v-list-item-title>
					</v-list-item-content>
				</v-list-item>
				<v-list-item v-if="item.Type == 'esg'">
					<v-list-item-content>
						<a :href="buildOrixeUrl('/esg/', item.Data.id)"><h5 class="secondary--text">Return to ESG Form</h5></a>
					</v-list-item-content>
				</v-list-item>
				<v-list-item v-if="item.Type == 'audit'">
					<v-list-item-content>
						<a :href="buildOrixeUrl('/audit/audits/', item.Data.id)"><h5 class="secondary--text">Return to Audit Form</h5></a>
					</v-list-item-content>
				</v-list-item>													
				<v-list-item v-if="item.Type == 'tx'">
					<v-list-item-content>
						<h5 class="secondary--text">Transaction ID</h5>
						<v-list-item-title>{{item.Data.id}}</v-list-item-title>
					</v-list-item-content>
					<v-list-item-content>
						<h5 class="secondary--text">Transaction Hash</h5>
						<v-list-item-title>{{item.Data.hash}}</v-list-item-title>
					</v-list-item-content>
					<v-list-item-content>
						<h5 class="secondary--text">Status</h5>
						<v-list-item-title>{{item.Data.status}}</v-list-item-title>
					</v-list-item-content>
					<v-list-item-content>
						<a :href="buildOrixeUrl('/trace/transactions/', item.Data.id)"><h5 class="secondary--text">Return to transaction details</h5></a>
					</v-list-item-content>					
				</v-list-item>
				<v-list-item v-else>
					<v-list-item-content>
						<h5 class="secondary--text">Data</h5>
						<v-list-item-title>
							<code class="d-block py-5 text-wrap">{{item.Data}}</code>
						</v-list-item-title>
					</v-list-item-content>
				</v-list-item>
			</v-col>
		</v-row>
		<error-dialog :error="error" />
		<v-dialog v-model="loading" hide-overlay persistent width="300">
			<v-card color="secondary" dark>
				<v-card-text class="text-center mx-auto">
					<ssc-loader />
					<p class="pt-3 white--text">loading block '{{ $route.params.id }}'</p>
				</v-card-text>
			</v-card>
		</v-dialog>
		
		<!-- Chain Transactions Dialog -->
		<v-dialog v-model="chainDialog" max-width="800" scrollable>
			<v-card>
				<v-card-title class="headline primary white--text">
					<v-icon left color="white">mdi-link</v-icon>
					Chain ID: {{ selectedChainID }}
				</v-card-title>
				<v-card-text>
					<v-progress-linear 
						v-if="searchingChain" 
						indeterminate 
						color="primary"
						class="mb-4"
					></v-progress-linear>
					
					<div v-if="!searchingChain && chainTransactions.length === 0" class="text-center py-4">
						<v-alert type="warning" outlined>
							<p class="mb-2"><strong>No transactions found with ChainID: {{ selectedChainID }}</strong></p>
							<p class="mb-0 text-caption">This could mean:</p>
							<ul class="text-left text-caption mt-2">
								<li>The ChainID is not stored in the blockchain data</li>
								<li>Transactions with this ChainID are in older blocks (beyond search limit)</li>
								<li>The ChainID field name might be different</li>
							</ul>
							<p class="text-caption mt-2">Check the browser console for detailed search logs.</p>
						</v-alert>
					</div>
					
					<div v-if="!searchingChain && chainTransactions.length > 0">
						<v-alert type="info" outlined class="mb-4">
							<strong>{{ chainTransactions.length }} transaction(s)</strong> found with ChainID <strong>{{ selectedChainID }}</strong>
							in <strong>{{ groupedByBlock.length }} block(s)</strong>
						</v-alert>
						
						<!-- Group by Block -->
						<v-expansion-panels v-model="expandedBlocks" multiple>
							<v-expansion-panel 
								v-for="blockGroup in groupedByBlock" 
								:key="blockGroup.blockIndex"
							>
								<v-expansion-panel-header>
									<v-row align="center" no-gutters>
										<v-col cols="auto">
											<v-icon color="primary" class="mr-2">mdi-cube</v-icon>
										</v-col>
										<v-col>
											<strong>Block #{{ blockGroup.blockIndex }}</strong>
											<span class="grey--text ml-2">({{ blockGroup.transactions.length }} transaction{{ blockGroup.transactions.length > 1 ? 's' : '' }})</span>
										</v-col>
										<v-col cols="auto">
											<v-chip x-small color="primary" class="mr-2">{{ blockGroup.timestamp }}</v-chip>
										</v-col>
									</v-row>
								</v-expansion-panel-header>
								<v-expansion-panel-content>
									<v-list>
										<v-list-item 
											v-for="(tx, txIndex) in blockGroup.transactions" 
											:key="txIndex"
											@click="navigateToBlock(tx.blockIndex)"
											style="cursor: pointer;"
											class="mb-1"
										>
											<v-list-item-content>
												<v-list-item-title>
													Transaction #{{ tx.transactionId }}
													<v-chip x-small :color="tx.status == 1 ? 'success' : 'warning'" class="ml-2">
														{{ tx.status == 1 ? 'Confirmed' : 'Pending' }}
													</v-chip>
												</v-list-item-title>
												<v-list-item-subtitle>
													<small class="grey--text">Hash: {{ tx.transactionHash.substring(0, 30) }}...</small>
												</v-list-item-subtitle>
											</v-list-item-content>
											<v-list-item-action>
												<v-icon>mdi-chevron-right</v-icon>
											</v-list-item-action>
										</v-list-item>
									</v-list>
									<v-btn 
										small 
										color="primary" 
										@click.stop="navigateToBlock(blockGroup.blockIndex)"
										class="mt-2"
									>
										<v-icon small left>mdi-open-in-new</v-icon>
										View Block #{{ blockGroup.blockIndex }}
									</v-btn>
								</v-expansion-panel-content>
							</v-expansion-panel>
						</v-expansion-panels>
					</div>
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn color="primary" text @click="chainDialog = false">Close</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-container>
</template>

<script>
import API from "../api/api"
import { getErrorMessage } from "../utils/errorMessage"
import getEnv from "../utils/env"
export default {
	name: "SingleBlock",

	data() {
		return {
			result: null,
			error: null,
			loading: false,
			dataunits: [],
			index: null,
			chainDialog: false,
			selectedChainID: null,
			chainTransactions: [],
			searchingChain: false,
			expandedBlocks: []
		};
	},
	mounted() {
		console.log(`[SingleBlock] Component mounted, loading block: ${this.$route.params.id}`);
		this.loading = true;
		//Dummy data
		this.loadBlock();
	},
	watch: {
		'$route'(to, from) {
			console.log(`[SingleBlock] Route changed from ${from.params.id} to ${to.params.id}`);
			this.loading = true;
			this.result = null;
			this.dataunits = [];
			this.error = null;
			this.loadBlock();
		}
	},
	computed: {
		groupedByBlock() {
			// Group transactions by block index
			const groups = {};
			for (const tx of this.chainTransactions) {
				if (!groups[tx.blockIndex]) {
					groups[tx.blockIndex] = {
						blockIndex: tx.blockIndex,
						blockHash: tx.blockHash,
						timestamp: tx.timestamp,
						transactions: []
					};
				}
				groups[tx.blockIndex].transactions.push(tx);
			}
			// Convert to array and sort by block index (newest first)
			return Object.values(groups).sort((a, b) => b.blockIndex - a.blockIndex);
		}
	},
	methods: {
		timestamp_text(time) {
			return this.$moment.unix(time).fromNow();
		},
		normalizeOrixeBaseUrl(rawUrl) {
			if (!rawUrl) {
				return null;
			}

			try {
				const parsedUrl = new URL(String(rawUrl).trim());
				const hostname = parsedUrl.hostname.toLowerCase();

				if (hostname.includes("staging")) {
					return "https://staging.orixe.tech";
				}

				if (hostname === "orixe.tech" || hostname.endsWith(".orixe.tech")) {
					return "https://orixe.tech";
				}

				return parsedUrl.origin.replace(/\/+$/, "");
			} catch (error) {
				return null;
			}
		},
		getBrowserOrixeBaseUrl() {
			if (typeof document !== "undefined" && document.referrer) {
				const normalizedReferrer = this.normalizeOrixeBaseUrl(document.referrer);
				if (normalizedReferrer === "https://staging.orixe.tech" || normalizedReferrer === "https://orixe.tech") {
					return normalizedReferrer;
				}
			}

			if (typeof window !== "undefined" && window.location && window.location.origin) {
				const normalizedCurrentUrl = this.normalizeOrixeBaseUrl(window.location.origin);
				if (normalizedCurrentUrl === "https://staging.orixe.tech" || normalizedCurrentUrl === "https://orixe.tech") {
					return normalizedCurrentUrl;
				}
			}

			return null;
		},
		getOrixeBaseUrl() {
			const browserBaseUrl = this.getBrowserOrixeBaseUrl();
			const configuredBaseUrl = getEnv("VUE_APP_WEB_URL") || getEnv("WEB_URL");
			if (configuredBaseUrl) {
				const candidates = String(configuredBaseUrl)
					.split(/[\r\n,]+/)
					.map(url => url.trim())
					.filter(Boolean);

				if (browserBaseUrl) {
					const matchingCandidate = candidates.find(url => {
						const normalized = this.normalizeOrixeBaseUrl(url);
						return normalized === browserBaseUrl;
					});

					if (matchingCandidate) {
						return this.normalizeOrixeBaseUrl(matchingCandidate);
					}
				}

				if (candidates.length > 0) {
					const normalizedCandidate = this.normalizeOrixeBaseUrl(candidates[0]);
					if (normalizedCandidate) {
						return normalizedCandidate;
					}
				}
			}

			if (browserBaseUrl) {
				return browserBaseUrl;
			}

			return "https://orixe.tech";
		},
		buildOrixeUrl(path, id) {
			const baseUrl = this.getOrixeBaseUrl().replace(/\/+$/, "");
			const normalizedPath = `/${String(path || "").replace(/^\/+|\/+$/g, "")}/`;
			const normalizedId = encodeURIComponent(String(id || "").replace(/^\/+|\/+$/g, ""));
			return `${baseUrl}${normalizedPath}${normalizedId}/`;
		},
		async showChainTransactions(chainID) {
			console.log(`[SingleBlock] Searching for transactions with ChainID: ${chainID}`);
			this.selectedChainID = chainID;
			this.chainDialog = true;
			this.chainTransactions = [];
			this.searchingChain = true;
			
			try {
				let offset = 0;
				const limit = 50;
				const maxBlocks = 2000; // Search up to 2000 blocks
				const foundTransactions = [];
				
				while (offset < maxBlocks) {
					const response = await API.get(`/api/blocks/?offset=${offset}&limit=${limit}`);
					
					if (!response.data.data || response.data.data.length === 0) {
						break;
					}
					
					for (const block of response.data.data) {
						if (block.DataUnits) {
							for (const du of block.DataUnits) {
								if (du.Type === 'tx' && du.Data) {
									// Handle both chainID and ChainID (case-insensitive comparison)
									const duChainID = du.Data.chainID || du.Data.ChainID || du.Data.chainid;
									
									// Debug: Log all transaction data keys for first few transactions
									if (offset === 0 && block.Index % 100 === 0) {
										console.log(`[SingleBlock] Debug - Transaction #${du.Data.id} chainID values:`, {
											chainID: du.Data.chainID,
											ChainID: du.Data.ChainID,
											chainid: du.Data.chainid
										});
									}
									
									if (duChainID && duChainID.toString().toUpperCase() === chainID.toString().toUpperCase()) {
										foundTransactions.push({
											transactionId: du.Data.id,
											transactionHash: du.Data.hash,
											status: du.Data.status,
											blockIndex: block.Index,
											blockHash: block.Hash,
											timestamp: this.$moment.unix(block.Timestamp).format('YYYY-MM-DD HH:mm:ss')
										});
									}
								}
							}
						}
					}
					
					offset += limit;
					
					// Update UI periodically for better UX
					if (foundTransactions.length > 0 && foundTransactions.length % 10 === 0) {
						this.chainTransactions = [...foundTransactions].sort((a, b) => b.transactionId - a.transactionId);
					}
					
					// Log progress every 100 blocks
					if (offset % 500 === 0) {
						console.log(`[SingleBlock] Searched ${offset} blocks, found ${foundTransactions.length} transactions so far`);
					}
				}
				
				// Sort by transaction ID (descending - newest first)
				this.chainTransactions = foundTransactions.sort((a, b) => b.transactionId - a.transactionId);
				
				
				// If no results, log some debug info
				if (this.chainTransactions.length === 0) {
					console.warn(`[SingleBlock] No transactions found with ChainID "${chainID}"`);
					console.warn(`[SingleBlock] Searched through ${offset} blocks`);
					console.warn(`[SingleBlock] This likely means ChainID is NOT included in blockchain data`);
					console.warn(`[SingleBlock] ChainID exists in MySQL but not in blockchain payload`);
					console.warn(`[SingleBlock] Solution: Update Orixe Platform to include ChainID when pushing transactions`);
					
					// Try to find any transaction to show what fields ARE available
					if (offset > 0) {
						
						try {
							const sampleResponse = await API.get(`/api/blocks/?offset=0&limit=10`);
							if (sampleResponse.data.data && sampleResponse.data.data.length > 0) {
								for (const block of sampleResponse.data.data) {
									if (block.DataUnits) {
										for (const du of block.DataUnits) {
											if (du.Type === 'tx' && du.Data) {
												console.log(`[SingleBlock] Sample transaction #${du.Data.id} fields:`, Object.keys(du.Data));
												console.log(`[SingleBlock] Sample transaction #${du.Data.id} data:`, du.Data);
												break;
											}
										}
									}
								}
							}
						} catch (e) {
							console.error(`[SingleBlock] Error getting sample:`, e);
						}
					}
				}
				
			} catch (err) {
				this.error = `Error searching for transactions with ChainID ${chainID}. Please try again.`;
			} finally {
				this.searchingChain = false;
			}
		},
		navigateToBlock(blockIndex) {
			this.chainDialog = false;
			this.$router.push({ path: `/blocks/${blockIndex}` });
		},
		async navigateToTransaction(txId) {
			this.loading = true;
			this.error = null;
			
			// Search through blocks to find the transaction
			let offset = 0;
			const limit = 50;
			const maxBlocks = 500; // Search up to 500 blocks
			let found = false;
			
			try {
				while (offset < maxBlocks && !found) {
					const response = await API.get(`/api/blocks/?offset=${offset}&limit=${limit}`);
					
					if (!response.data.data || response.data.data.length === 0) {
						break;
					}
					
					for (const block of response.data.data) {
						if (block.DataUnits) {
							for (const du of block.DataUnits) {
								if (du.Type === 'tx' && du.Data.id === txId) {
									this.$router.push({ path: `/blocks/${block.Index}` });
									found = true;
									this.loading = false;
									return;
								}
							}
						}
					}
					
					offset += limit;
				}
				
				if (!found) {
					this.error = `Transaction #${txId} not found in recent blocks. Try searching manually.`;
					this.loading = false;
				}
			} catch (err) {
				console.error(`[SingleBlock] Error searching for transaction:`, err);
				this.error = `Error searching for transaction #${txId}. Please try searching manually.`;
				this.loading = false;
			}
		},
		loadBlock() {
			this.loading = true;
			this.error = null;
			const blockId = this.$route.params.id;
			const idNum = parseInt(blockId, 10);
			// Allow block 0 (genesis) and any non-negative index
			if (String(idNum) === blockId && idNum < 0) {
				this.loading = false;
				this.error = "Block index cannot be negative.";
				return;
			}
			
			// Safety timeout to ensure loading doesn't get stuck
			const loadingTimeout = setTimeout(() => {
				if (this.loading) {
					console.error(`[SingleBlock] Loading timeout after 10 seconds`);
					this.loading = false;
					this.error = "Request timed out. Please try again.";
				}
			}, 10000);
			
			API
				.get("/api/blocks/" + blockId)
				.then(response => {
					clearTimeout(loadingTimeout);
					this.loading = false;
					
					if (!response.data) {
						this.error = "Invalid response from server";
						console.error("[SingleBlock] No response.data");
						return;
					}
					
					if (response.data.error && response.data.error.length > 0) {
						this.error = response.data.error[0].message;
						console.error("[SingleBlock] API error:", response.data.error);
						return;
					}
					
					if (!response.data.data || response.data.data.length < 1) {
						this.error = `Block ${blockId} not found. It may not exist yet or the index may be invalid.`;
						console.error("[SingleBlock] No block data in response");
						return;
					}
					
					this.result = response.data.data[0];
					if (!this.result || this.result.Index === undefined) {
						this.error = `Block ${blockId} not found. It may not exist yet or the index may be invalid.`;
						console.error("[SingleBlock] Block not found or null in response");
						return;
					}
					this.index = this.result.Index;
					// Ensure dataunits is always an array, even if DataUnits is null/undefined
					this.dataunits = this.result.DataUnits || [];
					if (this.dataunits.length > 0) {
						console.log(`[SingleBlock] First data unit:`, this.dataunits[0]);
					}
				})
				.catch(reason => {
					clearTimeout(loadingTimeout);
					console.error("[SingleBlock] Error loading block:", reason);
					console.error("[SingleBlock] Error details:", reason.response?.data || reason.message || reason);
					this.error = getErrorMessage(reason);
					this.loading = false;
				})
				.finally(() => {
					clearTimeout(loadingTimeout);
					// Ensure loading is always false
					if (this.loading) {
						this.loading = false;
					}
				});
		}
	}
};
</script>

<style scoped>
.v-list-item__subtitle {
	color: var(--v-secondary--base);
}
</style>